/**
 * ConfirmApplyModal
 *
 * Three-way confirmation dialog used from the product sidebar: apply to all
 * products, only this product, or cancel. All copy is passed in as props so
 * the same dialog serves different situations.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ConfirmApplyModalProps {
  isOpen: boolean;
  productName: string;
  title: string;
  message: string;
  applyAllLabel: string;
  applyProductLabel: string;
  cancelLabel?: string;
  onApplyAll: () => void | Promise<void>;
  onApplyProduct: () => void | Promise<void>;
  onCancel: () => void;
}

export function ConfirmApplyModal({
  isOpen,
  productName,
  title,
  message,
  applyAllLabel,
  applyProductLabel,
  cancelLabel,
  onApplyAll,
  onApplyProduct,
  onCancel,
}: ConfirmApplyModalProps) {
  const { t } = useTranslation();
  const primaryButtonRef = useRef<HTMLButtonElement>(null);
  const [isBusy, setIsBusy] = useState(false);

  // Reset busy state when modal closes
  useEffect(() => {
    if (!isOpen) setIsBusy(false);
  }, [isOpen]);

  // Wrap async handlers to prevent double-click
  const guardedApplyAll = useCallback(async () => {
    if (isBusy) return;
    setIsBusy(true);
    try {
      await onApplyAll();
    } finally {
      setIsBusy(false);
    }
  }, [isBusy, onApplyAll]);

  const guardedApplyProduct = useCallback(async () => {
    if (isBusy) return;
    setIsBusy(true);
    try {
      await onApplyProduct();
    } finally {
      setIsBusy(false);
    }
  }, [isBusy, onApplyProduct]);

  // Close on Escape
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    },
    [onCancel]
  );

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('keydown', handleKeyDown);
    // Auto-focus primary action
    primaryButtonRef.current?.focus();
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-apply-title"
      data-product-name={productName}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />

      {/* Modal */}
      <div className="relative bg-paper-2 rounded-card ring-1 ring-line max-w-md w-full mx-4 p-6">
        <h3
          id="confirm-apply-title"
          className="text-lg font-semibold text-ink mb-2"
        >
          {title}
        </h3>
        <p className="text-sm text-ink-2 mb-6">{message}</p>

        <div className="flex flex-col gap-2">
          <button
            ref={primaryButtonRef}
            onClick={guardedApplyAll}
            disabled={isBusy}
            className="w-full px-4 py-2.5 bg-primary text-primary-fg rounded-control font-medium hover:brightness-[0.92] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isBusy ? t('creator.confirmApply.saving') : applyAllLabel}
          </button>
          <button
            onClick={guardedApplyProduct}
            disabled={isBusy}
            className="w-full px-4 py-2.5 bg-paper-2 text-ink-2 border border-line rounded-control font-medium hover:bg-paper transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isBusy ? t('creator.confirmApply.saving') : applyProductLabel}
          </button>
          <button
            onClick={onCancel}
            disabled={isBusy}
            className="w-full px-4 py-2.5 text-mute rounded-control font-medium hover:text-ink-2 hover:bg-paper-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {cancelLabel ?? t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
